import { useEffect, useState } from "react";

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { HttpErrorCodesObj } from "../data";
import { Endpoints } from "../endpoints";
import { DefaultErrorNotification } from "../methods";
import { HttpMethodProps } from "../../types";
import { UploadFilesResponse } from "../responses";

interface RequestOptions {
  method: HttpMethodProps;
  route: string;
  body?: any;
  showError?: boolean;
}
export function usePerformRequest<Type>({
  method,
  route,
  body,
  showError = true,
}: RequestOptions) {
  const [data, setData] = useState<Type>();
  const [response, setResponse] = useState<AxiosResponse>();
  const [error, setError] = useState<AxiosError>();
  const [status, setStatus] = useState<number | string>("undefined");
  const [isLoading, setLoading] = useState<boolean>(false);
  const reloadData = async () => {
    const token = localStorage.getItem("t");
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `${token}`,
      },
      method,
      data: body,
      url: route,
    };
    setLoading(true);
    const r: any = await axios
      .request(config)
      .catch((e: AxiosError<Type, Type>) => {
        setError(e);
        setStatus(e.response?.status as number);

        if (e?.status === HttpErrorCodesObj.Forbidden) {
          // @ts-ignore
          if (e?.response && e.response?.data && e.response.data?.data[0]) {
            if (showError) {
              // @ts-ignore
              DefaultErrorNotification(e.response.data?.data[0]);
            }
          } else {
            if (showError) {
              // @ts-ignore
              DefaultErrorNotification(e.response?.data?.message);
            }
          }
        }
        return {
          status: e.response?.status,
        };
      });
    setResponse(r as AxiosResponse);
    setStatus(r.status);
    setLoading(false);
    setData(r?.data ?? undefined);
  };
  useEffect(() => {
    reloadData().catch(() => setLoading(false));
  }, [route]);

  return { data, response, isLoading, reloadData, status, error };
}

interface PerformRequestOptions {
  method: HttpMethodProps;
  data?: any;
  route: string;
  headers?: any;
  doNotUseAuthorization?: boolean;
  callback?: () => any;
}

export async function PerformRequest<Type>({
  method,
  data,
  route,
  headers,
  doNotUseAuthorization,
  callback,
}: PerformRequestOptions) {
  const token = localStorage.getItem("t");

  const requestHeaders = doNotUseAuthorization
    ? { ...headers }
    : { Authorization: `${token}`, ...headers };
  const config = {
    method,
    data,
    url: route,
    headers: requestHeaders,
  };

  const r: any = await axios.request(config).catch((e: AxiosError) => {
    if (callback) callback();
    return {
      status: e.response?.status,
      message: (e?.response?.data as any)?.message,
      data: (e?.response?.data as any)?.data,
    };
  });
  if (r.status === HttpErrorCodesObj.Forbidden) {
    DefaultErrorNotification(r?.data ? r.data[0] : r.message);
  }
  return {
    status: r.status as number,
    data: r?.data as Type,
    message: r?.message ?? undefined,
  };
}

export const UploadFiles = async (files: File[]) => {
  let data = new FormData();
  files.map((f) => {
    data.append("files", f);
  });
  const config: AxiosRequestConfig = {
    method: Endpoints.GetUploadFile().method,
    maxBodyLength: Infinity,
    url: Endpoints.GetUploadFile().route,
    data,
  };
  const r = await axios.request(config);
  const { data: response, status } = r;
  return {
    status,
    data: response.data,
    message: response.message,
  } as UploadFilesResponse;
};
